import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface InteractionState {
    favorite: boolean;
    wishlist: boolean;
}

interface RouteInteractionsContextType {
    interactions: Record<string, InteractionState>;
    fetchInteractionsForRoutes: (routeIds: string[]) => Promise<void>;
    setBulkInteractions: (data: Record<string, InteractionState>) => void;
    toggleFavorite: (routeId: string) => Promise<boolean>;
    toggleWishlist: (routeId: string) => Promise<boolean>;
    getInteraction: (routeId: string) => InteractionState;
    isLoading: boolean;
}

const RouteInteractionsContext = createContext<RouteInteractionsContextType | undefined>(undefined);

export function RouteInteractionsProvider({ children }: { children: ReactNode }) {
    const { isLoggedIn } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [interactions, setInteractions] = useState<Record<string, InteractionState>>({});
    const [isLoading, setIsLoading] = useState(false);

    // Filter out already fetched routes to avoid redundant calls
    const fetchInteractionsForRoutes = useCallback(async (routeIds: string[]) => {
        if (!isLoggedIn || routeIds.length === 0) return;

        // Skip IDs already fetched
        const newRouteIds = routeIds.filter(id => !interactions[id]);
        if (newRouteIds.length === 0) return;

        setIsLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            const res = await fetch('/api/interactions/bulk/routes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ routeIds: newRouteIds })
            });

            if (res.ok) {
                const data = await res.json();
                setInteractions(prev => ({ ...prev, ...data }));
            }
        } catch (error) {
            console.error("Error fetching bulk route interactions:", error);
        } finally {
            setIsLoading(false);
        }
    }, [isLoggedIn]);

    // Manual bulk update (e.g. from dashboard)
    const setBulkInteractions = useCallback((data: Record<string, InteractionState>) => {
        setInteractions(prev => ({ ...prev, ...data }));
    }, []);

    // Toggle Favorite
    const toggleFavorite = useCallback(async (routeId: string) => {
        if (!isLoggedIn) return false;

        const wasFavorite = interactions[routeId]?.favorite || false;
        setInteractions(prev => ({
            ...prev,
            [routeId]: { ...prev[routeId], favorite: !wasFavorite, wishlist: prev[routeId]?.wishlist || false }
        }));

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                // Revert if no session
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], favorite: wasFavorite }
                }));
                return false;
            }

            const res = await fetch(`/api/routes/${routeId}/favorite`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (res.ok) {
                const data = await res.json();
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], favorite: data.favorite }
                }));
                // Sync with dashboard and potentially routes list
                queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                queryClient.invalidateQueries({ queryKey: ["routes"] });
                return data.favorite;
            } else {
                console.error("Failed to toggle route favorite on server:", await res.text());
                // Revert on server error
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], favorite: wasFavorite }
                }));
            }
        } catch (error) {
            console.error("Error toggling route favorite:", error);
            setInteractions(prev => ({
                ...prev,
                [routeId]: { ...prev[routeId], favorite: wasFavorite }
            }));
        }
        return false;
    }, [isLoggedIn, interactions, queryClient]);

    // Toggle Wishlist
    const toggleWishlist = useCallback(async (routeId: string) => {
        if (!isLoggedIn) return false;

        const wasInWishlist = interactions[routeId]?.wishlist || false;
        setInteractions(prev => ({
            ...prev,
            [routeId]: { ...prev[routeId], wishlist: !wasInWishlist, favorite: prev[routeId]?.favorite || false }
        }));

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], wishlist: wasInWishlist }
                }));
                return false;
            }

            const res = await fetch(`/api/routes/${routeId}/wishlist`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (res.ok) {
                const data = await res.json();
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], wishlist: data.wishlist }
                }));
                queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                return data.wishlist;
            } else {
                console.error("Failed to toggle route wishlist on server:", await res.text());
                setInteractions(prev => ({
                    ...prev,
                    [routeId]: { ...prev[routeId], wishlist: wasInWishlist }
                }));
            }
        } catch (error) {
            console.error("Error toggling route wishlist:", error);
            setInteractions(prev => ({
                ...prev,
                [routeId]: { ...prev[routeId], wishlist: wasInWishlist }
            }));
        }
        return false;
    }, [isLoggedIn, interactions, queryClient]);

    const getInteraction = useCallback((routeId: string) => {
        return interactions[routeId] || { favorite: false, wishlist: false };
    }, [interactions]);

    return (
        <RouteInteractionsContext.Provider value={{
            interactions,
            fetchInteractionsForRoutes,
            setBulkInteractions,
            toggleFavorite,
            toggleWishlist,
            getInteraction,
            isLoading
        }}>
            {children}
        </RouteInteractionsContext.Provider>
    );
}

export function useRouteInteractions() {
    const context = useContext(RouteInteractionsContext);
    if (context === undefined) {
        throw new Error('useRouteInteractions must be used within a RouteInteractionsProvider');
    }
    return context;
}
